/**
 * Inventory Management API Client
 */

import { api } from '../api';
import type {
  Ingredient,
  InventoryAlert,
  InventoryDashboard,
  InventoryMovement,
  ProductRecipe,
  StockAdjustmentRequest,
  PurchaseRequest,
  FoodCostResponse,
} from '../../types/inventory';

const BASE_URL = '/api/inventory';

export const inventoryApi = {
  // ============= Dashboard =============

  getDashboard: async (): Promise<InventoryDashboard> => {
    const response = await api.get(`${BASE_URL}/dashboard`);
    return response.data;
  },

  // ============= Ingredients CRUD =============

  getIngredients: async (activeOnly?: boolean): Promise<Ingredient[]> => {
    const params = activeOnly !== undefined ? { activeOnly } : {};
    const response = await api.get(`${BASE_URL}/ingredients`, { params });
    return response.data;
  },

  getIngredientById: async (id: string): Promise<Ingredient> => {
    const response = await api.get(`${BASE_URL}/ingredients/${id}`);
    return response.data;
  },

  createIngredient: async (ingredient: Partial<Ingredient>): Promise<Ingredient> => {
    const response = await api.post(`${BASE_URL}/ingredients`, ingredient);
    return response.data;
  },

  updateIngredient: async (id: string, ingredient: Partial<Ingredient>): Promise<Ingredient> => {
    const response = await api.put(`${BASE_URL}/ingredients/${id}`, ingredient);
    return response.data;
  },

  deleteIngredient: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/ingredients/${id}`);
  },

  // ============= Stock Management =============

  adjustStock: async (id: string, request: StockAdjustmentRequest): Promise<Ingredient> => {
    const response = await api.post(`${BASE_URL}/ingredients/${id}/adjust-stock`, request);
    return response.data;
  },

  recordPurchase: async (id: string, request: PurchaseRequest): Promise<Ingredient> => {
    const response = await api.post(`${BASE_URL}/ingredients/${id}/record-purchase`, request);
    return response.data;
  },

  // ============= Alerts =============

  getAlerts: async (unresolvedOnly?: boolean): Promise<InventoryAlert[]> => {
    const params = unresolvedOnly !== undefined ? { unresolvedOnly } : {};
    const response = await api.get(`${BASE_URL}/alerts`, { params });
    return response.data;
  },

  resolveAlert: async (id: string): Promise<void> => {
    await api.post(`${BASE_URL}/alerts/${id}/resolve`);
  },

  // ============= Analytics =============

  getLowStockIngredients: async (): Promise<Ingredient[]> => {
    const response = await api.get(`${BASE_URL}/analytics/low-stock`);
    return response.data;
  },

  getInventoryValue: async (): Promise<{ totalValue: number; currency: string }> => {
    const response = await api.get(`${BASE_URL}/analytics/value`);
    return response.data;
  },

  getMovementHistory: async (days?: number): Promise<InventoryMovement[]> => {
    const params = days !== undefined ? { days } : {};
    const response = await api.get(`${BASE_URL}/analytics/movements`, { params });
    return response.data;
  },

  // ============= Recipes =============

  getProductRecipes: async (productId: string): Promise<ProductRecipe[]> => {
    const response = await api.get(`${BASE_URL}/recipes/product/${productId}`);
    return response.data;
  },

  createRecipe: async (recipe: Partial<ProductRecipe>): Promise<ProductRecipe> => {
    const response = await api.post(`${BASE_URL}/recipes`, recipe);
    return response.data;
  },

  deleteRecipe: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/recipes/${id}`);
  },

  getProductFoodCost: async (productId: string): Promise<FoodCostResponse> => {
    const response = await api.get(`${BASE_URL}/recipes/product/${productId}/food-cost`);
    return response.data;
  },
};
